import { HTMLProps, MouseEventHandler, useContext } from 'react';

import { styled } from '@mui/material';
import { IconContext, MagnifyingGlass, X } from 'phosphor-react';

import { ListContext } from './List';
import { ListItem, ListItemProps } from './ListItem';
import { WithRef } from '../../types';
import { IconButton } from '../buttons';
import { Input, InputProps } from '../controls/input';

type ClearButtonProps = {
  onClick?: MouseEventHandler<HTMLButtonElement> | undefined;
};

export type ListSearchInputProps = InputProps & {
  showClearButton?: boolean;
  listItemProps?: ListItemProps;
  clearButtonProps?: ClearButtonProps;
};

const sizeToIconSize = {
  small: '16px',
  medium: '16px',
  large: '20px',
};

// TODO: map to IconButton sizes until Input/List migrate to the Sizes<> type
const sizeToIconButtonSize = {
  small: 'sm',
  medium: 'md',
  large: 'lg',
} as const;

const mergeInputProps = (
  { onMouseDown, ...inputProps }: HTMLProps<HTMLInputElement>,
  disabled?: boolean,
  readOnly?: boolean
): HTMLProps<HTMLInputElement> => {
  return {
    ...inputProps,
    onMouseDown:
      disabled || readOnly
        ? undefined
        : onMouseDown
        ? (e) => {
            onMouseDown(e);
          }
        : onMouseDown,
  };
};

const StyledInput = styled(Input, {
  name: 'DsListSearchInput',
  slot: 'Root',
  target: 'DsListSearchInput-root',
  overridesResolver: ({ size }, styles) => {
    return [styles.root, styles[size]];
  },
})<InputProps>({});

StyledInput.displayName = 'StyledInput';

export const ListSearchInput = ({
  ref,
  inputProps = {},
  listItemProps = {},
  clearButtonProps,
  showClearButton,
  testId,
  ...props
}: WithRef<HTMLInputElement, ListSearchInputProps>) => {
  const listContext = useContext(ListContext);
  const size = listContext?.size || props.size || 'medium';

  const clearInput = showClearButton &&
    !!clearButtonProps &&
    (!!inputProps.value || !!props.value) && (
      <IconButton
        {...clearButtonProps}
        className="clearInput"
        testId="clear-input-btn"
        size={sizeToIconButtonSize[size]}
        onClick={!props.disabled ? clearButtonProps.onClick : undefined}
      >
        <X weight="fill" height={18} width={18} />
      </IconButton>
    );

  return (
    <ListItem disableHover disableGutters testId={testId} {...listItemProps}>
      <IconContext.Provider value={{ size: sizeToIconSize[size] }}>
        <StyledInput
          {...props}
          size={size}
          inputProps={{
            ...mergeInputProps(inputProps, props.disabled, props.readOnly),
            ref,
          }}
          startOutsideAdornment={<MagnifyingGlass />}
          endOutsideAdornment={clearInput}
        />
      </IconContext.Provider>
    </ListItem>
  );
};

ListSearchInput.displayName = 'ListSearchInput';
